import { useEffect, useRef } from 'react';

/**
 * Trap keyboard focus within a container while it's mounted.
 *
 * Why this exists:
 *   * Modal dialogs without a focus trap let Tab walk out into the
 *     page behind them — a screen-reader user can't tell what's
 *     focusable, and a sighted keyboard user lands on hidden controls
 *     that visually appear "behind" the modal.
 *   * The dialog also needs to send focus *into* itself on open and
 *     restore focus to the previously focused element on close, or
 *     the keyboard user gets dumped at the top of the page.
 *
 * Pattern: pass `active` to enable the trap, plus a ref to the
 * container. Returns nothing — purely a side-effect hook.
 *
 * Notes:
 *   * Tab cycles forward, Shift+Tab cycles backward.
 *   * Escape is NOT handled here — owners decide what closes the
 *     dialog (some need a confirm step). Wire `onKeyDown` on the
 *     container if you want Escape to close.
 *   * If there are no focusable children the container itself gets
 *     focus, so Tab is at least swallowed instead of leaking.
 */
export function useFocusTrap(active: boolean, ref: React.RefObject<HTMLElement>): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const container = ref.current;
    if (!container) return undefined;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] => {
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'audio[controls]',
        'video[controls]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      // Filter `aria-hidden` and `tabindex=-1` (the latter shows up
      // as a separate selector exclusion above already, but a stray
      // `tabindex="-1"` on an `<a href>` would slip through). We
      // intentionally don't use `offsetParent` for visibility — it's
      // unreliable in JSDOM (always null) and rarely needed in
      // practice, since explicitly hidden content tends to also be
      // `aria-hidden` or `disabled`.
      return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => !el.closest('[aria-hidden="true"]') && el.getAttribute('tabindex') !== '-1',
      );
    };

    // Send focus into the dialog. If the container has an autofocus
    // child, prefer that; otherwise focus the first focusable; if
    // none, focus the container itself.
    const auto = container.querySelector<HTMLElement>('[data-autofocus]');
    const initial = auto ?? focusables()[0] ?? container;
    if (!container.hasAttribute('tabindex') && initial === container) {
      container.setAttribute('tabindex', '-1');
    }
    initial.focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      // Restore focus on unmount so the keyboard user lands back
      // wherever they were before the trap activated.
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [active, ref]);
}
